import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { Alert, Maps } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { colorTokens } from '@rideiq/shared/components/variables';

import { LocationFormData } from '../../../types';

import { draggableMarker, preciseMarkerSvg, staticMarkerSvg } from './constants';
import {
  getPlaceLatLngByPlaceId,
  INITIAL_ZOOM_LEVEL,
  SET_PIN_ZOOM_LEVEL,
  SETTING_PIN_ZOOM_LEVEL,
} from './LocationMap.helpers';
import { MapContainer } from './styles';
import { useMapCenter } from './useMapCenter';

interface LocationMapProps {
  control: Control<LocationFormData>;
  isModal?: boolean;
  canDragMarker: boolean;
  onCancel: () => void;
  onApply: (position: { lat: number; lng: number }) => void;
  onSetup?: () => void;
  coordinates?: { lat: number; lng: number };
  isExistingLocation?: boolean;
}

export const LocationMap: FC<LocationMapProps> = ({
  control,
  isModal,
  canDragMarker,
  onCancel,
  onApply,
  onSetup,
  coordinates,
  isExistingLocation,
}) => {
  const isMountedRef = useRef(true);
  const [mapInstance, setMapInstance] = useState<google.maps.Map>();
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number }>();
  const [markerPrecisePosition, setMarkerPrecisePosition] = useState<{ lat: number; lng: number }>();
  const [markerInstance, setMarkerInstance] = useState<google.maps.Marker>();
  const watchAddressPlaceId = useWatch({
    control,
    name: 'placeId',
    defaultValue: '',
  });
  const watchPreciseLocation = useWatch({
    control,
    name: 'preciseLocation',
    defaultValue: undefined,
  });
  const { center } = useMapCenter({
    locationCoordinates: watchPreciseLocation || markerPosition,
    mapInstance: mapInstance,
  });

  const getMarkerIcon = () => {
    let pinSvg;

    if (canDragMarker) {
      pinSvg = draggableMarker;
    } else {
      pinSvg = watchPreciseLocation ? preciseMarkerSvg : staticMarkerSvg;
    }

    return {
      icon: {
        url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(pinSvg),
        anchor: new google.maps.Point(21, 56),
      },
    };
  };

  const getPlaceIdCoordinates = useCallback(async () => {
    if (watchAddressPlaceId && mapInstance) {
      const response = await getPlaceLatLngByPlaceId(watchAddressPlaceId, mapInstance);

      response && setMarkerPosition(response);
    }
  }, [getPlaceLatLngByPlaceId, mapInstance, watchAddressPlaceId]);

  const handleOnCancel = () => {
    onCancel();
    if (markerPosition && markerInstance) {
      markerInstance?.setPosition(watchPreciseLocation || markerPosition);
    }
  };

  const handleOnApply = () => {
    if (markerPrecisePosition) {
      onApply(markerPrecisePosition);
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isExistingLocation) {
      const locationCoordinates = watchPreciseLocation || coordinates;

      setMarkerPosition(locationCoordinates);
    } else if (watchAddressPlaceId) {
      getPlaceIdCoordinates();
    } else {
      markerPosition && setMarkerPosition(undefined);
    }
  }, [watchPreciseLocation, watchAddressPlaceId, getPlaceIdCoordinates, isExistingLocation, coordinates]);

  useEffect(() => {
    // if the precise location got reset(clicked cancel), reset the marker position
    // this is ONLY triggerd while setting the precise pin, not clicking Reset button
    if (!watchPreciseLocation && markerPrecisePosition) {
      markerInstance?.setPosition(markerPosition);
      setMarkerPrecisePosition(undefined);
    }
  }, [watchPreciseLocation]);

  useEffect(() => {
    canDragMarker ? mapInstance?.setZoom(SETTING_PIN_ZOOM_LEVEL) : mapInstance?.setZoom(SET_PIN_ZOOM_LEVEL);
    // this will make the map zoom in on the correct marker position if the user changes anything on the map
    markerPosition && mapInstance?.panTo(watchPreciseLocation || markerPosition);
  }, [canDragMarker, mapInstance]);

  return (
    <MapContainer>
      <Maps
        googleMapsApiKey={window.env?.RIDEIQ_GOOGLE_MAPS_API_KEY}
        handleMapInstance={(instance) => isMountedRef.current && setMapInstance(instance)}
        center={center}
        zoom={INITIAL_ZOOM_LEVEL}
        height={isModal ? '644px' : '745px'}
        showLayerToggle={true}
        options={{
          tilt: 0, // Force top-down view (no 3D perspective)
          heading: 0, // North-up orientation
          cameraControlOptions: {
            position: 3,
          },

          mapTypeId: 'hybrid',
        }}
        {...(canDragMarker && {
          mapContainerStyle: {
            borderRadius: '4px',
            border: `3px solid ${colorTokens.secondary.light}`,
          },
        })}
      >
        {markerPosition && mapInstance && (
          <Maps.DraggableMarker
            handleMarkerInstance={(marker) => isMountedRef.current && setMarkerInstance(marker)}
            {...getMarkerIcon()}
            position={watchPreciseLocation || markerPosition}
            draggable={canDragMarker}
            onMouseUp={(nextPosition) => nextPosition && setMarkerPrecisePosition(nextPosition.latLng?.toJSON())}
          />
        )}
        {/* No FF is needed here since the onSetup will only get called by the LocationForm */}
        {onSetup && !canDragMarker && !watchPreciseLocation && markerPosition && (
          <Alert
            style={{ position: 'relative', margin: '10px' }}
            severity="info"
            title="Add more precision to your location"
            message="Using a precise pin helps your CareDriver get to the right spot quickly for a smooth pickup and dropoff."
            action={
              <Button color="primary" size="small" onClick={() => onSetup()}>
                Try it!
              </Button>
            }
          />
        )}

        {canDragMarker && (
          <Alert
            style={{ position: 'relative', margin: '10px' }}
            severity="info"
            message="Click and drag the pin to the precise position of the pickup/drop-off point."
            action={
              <>
                <Button variant="contained" color="tertiary" size="small" onClick={() => handleOnCancel()}>
                  Cancel
                </Button>
                <Button color="primary" size="small" onClick={() => handleOnApply()}>
                  Apply
                </Button>
              </>
            }
          />
        )}
      </Maps>
    </MapContainer>
  );
};
